import { useMemo, useState } from 'react';
import { DRILL_LIBRARY } from '@4cm/core/drills/library';
import type { DrillCorner, DrillTemplate } from '@4cm/core/drills/library';
import { DrillCanvasPreview } from '../DrillCanvasPreview';
import { glassBackdrop, glassModal } from './liquidGlass';
import { cornerCssVar } from '../../lib/cornerColors';

interface LibrarySheetProps {
  open: boolean;
  onClose: () => void;
  /** Called when the coach picks a template. Receives the full
   *  DrillTemplate so the parent can apply spec.entities / spec.cones /
   *  spec.arrows additively onto the active drill canvas. */
  onPick: (template: DrillTemplate) => void;
}

const CORNER_OPTIONS: { id: 'all' | DrillCorner; label: string; color?: string }[] = [
  { id: 'all', label: 'All corners' },
  { id: 'technical_tactical', label: 'Technical / Tactical', color: cornerCssVar('technical_tactical') },
  { id: 'physical', label: 'Physical', color: cornerCssVar('physical') },
  { id: 'psychological', label: 'Psychological', color: cornerCssVar('psychological') },
  { id: 'social', label: 'Social', color: cornerCssVar('social') },
];

const PHASE_OPTIONS: { id: 'all' | 'Foundation' | 'Progression' | 'Performance'; label: string }[] = [
  { id: 'all', label: 'All phases' },
  { id: 'Foundation', label: 'Foundation' },
  { id: 'Progression', label: 'Progression' },
  { id: 'Performance', label: 'Performance' },
];

/**
 * Drill library picker — modal sheet showing the 12 hand-authored
 * templates with corner + phase filters. Pick → host applies the
 * template's spec to the current canvas (additive merge so the coach
 * doesn't lose work).
 */
export function LibrarySheet({ open, onClose, onPick }: LibrarySheetProps): React.JSX.Element | null {
  const [cornerFilter, setCornerFilter] = useState<'all' | DrillCorner>('all');
  const [phaseFilter, setPhaseFilter] = useState<'all' | 'Foundation' | 'Progression' | 'Performance'>('all');

  const filtered = useMemo(() => {
    return DRILL_LIBRARY.filter((t) => {
      if (cornerFilter !== 'all' && t.primaryCorner !== cornerFilter) return false;
      if (phaseFilter !== 'all' && !t.ageGroups.includes(phaseFilter)) return false;
      return true;
    });
  }, [cornerFilter, phaseFilter]);

  if (!open) return null;

  return (
    <div role="dialog" aria-label="Drill library" style={backdropStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Drill library</div>
            <h2 style={titleStyle}>Pick a starting point</h2>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close library">✕</button>
        </header>

        <div style={filterBarStyle}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CORNER_OPTIONS.map((c) => {
              const active = cornerFilter === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCornerFilter(c.id)}
                  style={{
                    ...chipBtn,
                    background: active ? (c.color ?? 'rgba(255,255,255,0.18)') : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${active ? (c.color ?? 'rgba(255,255,255,0.4)') : 'rgba(255,255,255,0.15)'}`,
                  }}
                  aria-pressed={active}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {PHASE_OPTIONS.map((p) => {
              const active = phaseFilter === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPhaseFilter(p.id)}
                  style={{
                    ...chipBtn,
                    background: active ? 'rgba(39,174,96,0.25)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${active ? '#27ae60' : 'rgba(255,255,255,0.15)'}`,
                  }}
                  aria-pressed={active}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={gridWrapStyle}>
          {filtered.length === 0 ? (
            <p style={emptyStyle}>No templates match those filters. Drop a filter to see more.</p>
          ) : (
            <div style={gridStyle}>
              {filtered.map((t) => (
                <TemplateCard key={t.id} template={t} onPick={() => onPick(t)} />
              ))}
            </div>
          )}
        </div>

        <footer style={footerStyle}>
          <span style={{ flex: 1, fontSize: 11, opacity: 0.7 }}>
            {filtered.length} of {DRILL_LIBRARY.length} templates. Picking a template merges its
            players + cones + arrows into the current canvas (additive — your work stays).
          </span>
        </footer>
      </div>
    </div>
  );
}

interface TemplateCardProps {
  template: DrillTemplate;
  onPick: () => void;
}

function TemplateCard({ template, onPick }: TemplateCardProps): React.JSX.Element {
  const cornerColor = cornerColorFor(template.primaryCorner);
  return (
    <button type="button" onClick={onPick} style={cardStyle}>
      <div style={thumbStyle}>
        <DrillCanvasPreview spec={template.spec} width={220} />
      </div>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...cornerBadgeStyle, background: cornerColor }} aria-hidden="true" />
          <strong style={{ fontSize: 13 }}>{template.name}</strong>
        </div>
        <p style={cardDescStyle}>{template.description}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 10, opacity: 0.8 }}>
          <span style={metaPillStyle}>{template.durationMins} min</span>
          <span style={metaPillStyle}>{template.playersRequired}+ players</span>
          {template.ageGroups.map((a) => <span key={a} style={metaPillStyle}>{a}</span>)}
        </div>
      </div>
    </button>
  );
}

function cornerColorFor(c: DrillCorner): string {
  return cornerCssVar(c);
}

// ---- styles ---------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  ...glassBackdrop,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const sheetStyle: React.CSSProperties = {
  ...glassModal,
  width: 'min(960px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex', flexDirection: 'column',
  fontFamily: 'system-ui, sans-serif',
};

const headerStyle: React.CSSProperties = {
  padding: '14px 16px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const eyebrowStyle: React.CSSProperties = {
  fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.55,
};

const titleStyle: React.CSSProperties = {
  margin: 0, fontSize: 18, fontWeight: 600,
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent', color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
  width: 32, height: 32, cursor: 'pointer',
};

const filterBarStyle: React.CSSProperties = {
  padding: 12, borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const chipBtn: React.CSSProperties = {
  padding: '4px 10px', fontSize: 11,
  color: '#fff', borderRadius: 999, cursor: 'pointer',
  fontFamily: 'inherit',
};

const gridWrapStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: 16,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 12,
};

const emptyStyle: React.CSSProperties = {
  textAlign: 'center', opacity: 0.7, padding: 32, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  cursor: 'pointer', overflow: 'hidden',
  textAlign: 'left',
  fontFamily: 'inherit',
};

const thumbStyle: React.CSSProperties = {
  width: '100%', background: '#0a1628',
  padding: 4, display: 'flex', justifyContent: 'center',
};

const cardDescStyle: React.CSSProperties = {
  margin: 0, fontSize: 11, opacity: 0.8, lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const cornerBadgeStyle: React.CSSProperties = {
  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
};

const metaPillStyle: React.CSSProperties = {
  padding: '2px 6px',
  background: 'rgba(255,255,255,0.08)', borderRadius: 999,
};

const footerStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderTop: '1px solid rgba(255,255,255,0.1)',
  display: 'flex', alignItems: 'center', gap: 10,
};
